<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardCheck,
  ShieldAlert,
  X,
} from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import LogFilterBar from '@/components/LogFilterBar.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'
import AppLayout from '@/layouts/AppLayout.vue'
import { highlightXml } from '@/lib/syntax-highlight'
import { formatAmount, formatDate, formatDateTime } from '@/lib/utils'
import { editLoanApplicationPath, equifaxScoreRequestsPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'

interface EquifaxScoreRequest {
  id: number
  loan_application_id: string
  first_name: string
  last_name: string
  date_of_birth: string
  address: string
  amount: number
  error_code: string | null
  error_message: string | null
  requested_at: string
  raw_response: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  error_code?: string | null
  date_from?: string | null
  date_to?: string | null
}

const props = defineProps<{
  requests: EquifaxScoreRequest[]
  error_codes: string[]
  pagination: PaginationMeta
  filters: Filters
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Failed Credit Checks', href: equifaxScoreRequestsPath() }]

const ALL = '__all__'

const search = ref(props.filters.q ?? '')
const errorCodeFilter = ref(props.filters.error_code ?? ALL)
const dateFrom = ref(props.filters.date_from ?? '')
const dateTo = ref(props.filters.date_to ?? '')
const expandedId = ref<number | null>(null)
const { copy, isCopied } = useCopyToClipboard()

const hasActiveFilters = computed(
  () => search.value !== '' || errorCodeFilter.value !== ALL || dateFrom.value !== '' || dateTo.value !== '',
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  error_code: errorCodeFilter.value !== ALL ? errorCodeFilter.value : undefined,
  date_from: dateFrom.value || undefined,
  date_to: dateTo.value || undefined,
}))

function applyFilters() {
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(equifaxScoreRequestsPath(), params, { preserveState: true, replace: true })
}

let searchTimer: ReturnType<typeof setTimeout>

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch([errorCodeFilter, dateFrom, dateTo], applyFilters)

onUnmounted(() => clearTimeout(searchTimer))

function clearFilters() {
  search.value = ''
  errorCodeFilter.value = ALL
  dateFrom.value = ''
  dateTo.value = ''
}

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id
}

async function copyRaw(req: EquifaxScoreRequest) {
  await copy(req.raw_response, req.id)
}

const tableColumns: TableColumn[] = [
  { key: 'expand', label: '', headerClass: 'w-8' },
  { key: 'application', label: 'Application' },
  { key: 'customer', label: 'Customer' },
  { key: 'amount', label: 'Amount' },
  { key: 'error', label: 'Error' },
  { key: 'requested', label: 'Requested' },
]

</script>

<template>
  <Head title="Failed Credit Checks" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <ShieldAlert class="text-muted-foreground size-6" />
        <div>
          <h1 class="text-foreground text-xl font-semibold">Failed Credit Checks</h1>
          <p class="text-muted-foreground text-sm">
            Equifax score requests that exhausted all retries — retained for monthly refund claims
          </p>
        </div>
      </div>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All Failed Requests</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} record{{ pagination.total_count !== 1 ? 's' : '' }}
                {{ hasActiveFilters ? 'found' : 'total' }}
              </CardDescription>
            </div>
            <AlertTriangle class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Filters -->
        <LogFilterBar
          v-model:search="search"
          v-model:date-from="dateFrom"
          v-model:date-to="dateTo"
          :has-active-filters="hasActiveFilters"
          search-placeholder="Search by application ID or name…"
          date-prefix-label="Requested:"
          @clear="clearFilters"
        >
          <Select v-model="errorCodeFilter">
            <SelectTrigger class="w-full sm:w-44">
              <SelectValue placeholder="Any error code" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any error code</SelectItem>
              <SelectItem v-for="code in error_codes" :key="code" :value="code">{{ code }}</SelectItem>
            </SelectContent>
          </Select>
        </LogFilterBar>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div v-if="requests.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <ShieldAlert class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? 'No records match your filters' : 'No failed credit checks — great news!' }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable v-else :columns="tableColumns" compact table-fixed>
            <template v-for="req in requests" :key="req.id">
              <!-- Summary row -->
              <tr
                class="border-border cursor-pointer border-b transition-colors select-none"
                :class="expandedId === req.id ? 'bg-muted/60' : 'hover:bg-muted/40'"
                @click="toggleExpand(req.id)"
              >
                <td class="px-3 py-4 text-center">
                  <ChevronDown
                    v-if="expandedId === req.id"
                    class="text-muted-foreground size-4 transition-transform"
                  />
                  <ChevronRight v-else class="text-muted-foreground size-4" />
                </td>
                <td class="px-4 py-4 font-mono text-xs font-medium">{{ req.loan_application_id }}</td>
                <td class="px-4 py-4 font-medium">{{ req.first_name }} {{ req.last_name }}</td>
                <td class="text-muted-foreground px-4 py-4">{{ formatAmount(req.amount) }}</td>
                <td class="px-4 py-4">
                  <Badge v-if="req.error_code" variant="destructive">{{ req.error_code }}</Badge>
                  <span v-else class="text-muted-foreground text-xs">—</span>
                </td>
                <td class="text-muted-foreground px-4 py-4 whitespace-nowrap">
                  {{ formatDateTime(req.requested_at) }}
                </td>
              </tr>

              <!-- Expanded detail row -->
              <tr v-if="expandedId === req.id" class="border-border border-b">
                <td colspan="6" class="bg-muted/30 px-6 py-5">
                  <div class="flex flex-col gap-5">
                    <!-- Detail grid -->
                    <div class="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Application ID
                        </p>
                        <a
                          :href="editLoanApplicationPath(req.loan_application_id)"
                          class="mt-0.5 font-mono text-xs hover:underline"
                          target="_blank"
                          @click.stop
                          >{{ req.loan_application_id }}</a
                        >
                      </div>
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Customer</p>
                        <p class="mt-0.5 text-sm">{{ req.first_name }} {{ req.last_name }}</p>
                      </div>
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Date of Birth
                        </p>
                        <p class="mt-0.5 text-sm">{{ formatDate(req.date_of_birth) }}</p>
                      </div>
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Amount</p>
                        <p class="mt-0.5 text-sm">{{ formatAmount(req.amount) }}</p>
                      </div>
                      <div class="col-span-2 sm:col-span-2">
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Address</p>
                        <p class="mt-0.5 text-sm">{{ req.address }}</p>
                      </div>
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Requested At
                        </p>
                        <p class="mt-0.5 text-sm">{{ formatDateTime(req.requested_at) }}</p>
                      </div>
                      <div v-if="req.error_code">
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Error Code</p>
                        <p class="text-destructive mt-0.5 text-sm font-medium">{{ req.error_code }}</p>
                      </div>
                      <div v-if="req.error_message" class="col-span-2 lg:col-span-3">
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Error Message
                        </p>
                        <p class="text-destructive mt-0.5 text-sm">{{ req.error_message }}</p>
                      </div>
                    </div>

                    <!-- Raw response -->
                    <div>
                      <div class="mb-1.5 flex items-center justify-between">
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Raw Response
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="text-muted-foreground hover:text-foreground h-6 gap-1.5 px-2 text-xs"
                          @click.stop="copyRaw(req)"
                        >
                          <ClipboardCheck v-if="isCopied(req.id)" class="size-3.5 text-green-600" />
                          <Clipboard v-else class="size-3.5" />
                          {{ isCopied(req.id) ? 'Copied' : 'Copy' }}
                        </Button>
                      </div>
                      <pre
                        class="bg-background border-border overflow-x-auto rounded-md border p-3 font-mono text-xs leading-relaxed"
                        v-html="highlightXml(req.raw_response)"></pre
                      >
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="equifaxScoreRequestsPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
